import { useEffect } from 'react';
import Lenis from 'lenis';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

// Initialise Lenis smooth scroll and wire it to GSAP ScrollTrigger.
// Also sets up an IntersectionObserver that adds `is-inview` to any element
// with `.reveal-line` or `.reveal-up`, triggering CSS-driven cinematic reveals.
export default function useSmoothScroll() {
  useEffect(() => {
    const lenis = new Lenis({
      duration: 1.25,
      smoothWheel: true,
      lerp: 0.085,
      wheelMultiplier: 1,
      touchMultiplier: 1.4,
      easing: (t) => 1 - Math.pow(1 - t, 3),
    });
    // Expose Lenis so floating UI (ScrollNav etc.) can use the smooth scroll
    window.__lenis = lenis;

    function raf(time) {
      lenis.raf(time);
      ScrollTrigger.update();
    }
    const id = gsap.ticker.add((time) => raf(time * 1000));
    gsap.ticker.lagSmoothing(0);

    // Refresh ScrollTrigger once Lenis is wired so pinned sections measure correctly
    const refreshTimer = setTimeout(() => ScrollTrigger.refresh(), 50);

    // Anchor link smooth scroll via lenis
    const onAnchorClick = (e) => {
      const target = e.target.closest('a[href^="#"]');
      if (!target) return;
      const href = target.getAttribute('href');
      if (!href || href === '#') return;
      const el = document.querySelector(href);
      if (!el) return;
      e.preventDefault();
      lenis.scrollTo(el, { offset: -64, duration: 1.4 });
    };
    document.addEventListener('click', onAnchorClick);

    // IntersectionObserver for reveal classes. Custom pages arrive after
    // /api/content has loaded, so new reveal nodes must be observed too.
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-inview');
            io.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.15, rootMargin: '0px 0px -8% 0px' }
    );
    const observed = new WeakSet();
    const observeRevealNode = (node) => {
      if (!(node instanceof Element)) return;
      if (node.matches('.reveal-line, .reveal-up') && !observed.has(node)) {
        observed.add(node);
        io.observe(node);
      }
      node.querySelectorAll?.('.reveal-line, .reveal-up').forEach((child) => {
        if (!observed.has(child)) {
          observed.add(child);
          io.observe(child);
        }
      });
    };
    observeRevealNode(document.body);

    const mutationObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach(observeRevealNode);
      });
    });
    mutationObserver.observe(document.body, { childList: true, subtree: true });

    return () => {
      document.removeEventListener('click', onAnchorClick);
      io.disconnect();
      mutationObserver.disconnect();
      clearTimeout(refreshTimer);
      gsap.ticker.remove(id);
      lenis.destroy();
      if (window.__lenis === lenis) delete window.__lenis;
    };
  }, []);
}
